// The account menu at the top-right: who is signed in, the theme, settings,
// help and sign-out (docs/plans/shell-menus.md).
import { Menu, MenuItem, MenuSeparator, Modal, cx } from '@ims/ui'
import {
  Keyboard,
  LifeBuoy,
  LogOut,
  Mail,
  Monitor,
  Moon,
  Phone,
  Settings,
  Smartphone,
  Sun,
  UserRound,
} from '@ims/ui/icons'
import { useState } from 'react'
import { useNavigate } from 'react-router'
import { useSettings } from '../hooks.ts'
import { accountName, initialsOf } from '../lib/account.ts'
import { SUPPORT_EMAIL, SUPPORT_PHONE_LABEL, supportTel } from '../lib/support.ts'
import { setDateChoice } from '../lib/calendar.ts'
import { useMotion, type MotionChoice } from '../lib/motion.ts'
import { useTheme, type ThemeChoice } from '../lib/theme.ts'
import { useUser } from '../session.tsx'
import { useWorkspace } from '../workspace.tsx'
import { useSignOut } from './sign-out.tsx'

const THEMES: { value: ThemeChoice; label: string; icon: typeof Sun }[] = [
  { value: 'system', label: 'System', icon: Monitor },
  { value: 'light', label: 'Light', icon: Sun },
  { value: 'dark', label: 'Dark', icon: Moon },
]

function ThemeSwitch() {
  const [theme, setTheme] = useTheme()
  return (
    <div
      role="radiogroup"
      aria-label="Theme"
      className="mx-1 mb-1 grid grid-cols-3 gap-1 rounded-xl border border-border bg-background p-1"
    >
      {THEMES.map(({ value, label, icon: Icon }) => (
        <button
          key={value}
          type="button"
          role="radio"
          aria-checked={theme === value}
          tabIndex={-1}
          onClick={() => setTheme(value)}
          className={cx(
            'flex items-center justify-center gap-1.5 rounded-lg py-1.5 text-xs font-medium transition-colors focus-visible:outline-2 focus-visible:outline-primary',
            theme === value ? 'bg-surface text-ink shadow-card' : 'text-muted hover:text-ink',
          )}
        >
          <Icon className="size-3.5" />
          {label}
        </button>
      ))}
    </div>
  )
}

const DATES = [
  { value: 'bs', label: 'BS', title: 'Nepali dates' },
  { value: 'ad', label: 'AD', title: 'English dates' },
  { value: 'both', label: 'Both', title: 'Nepali and English' },
] as const

/** How dates read on this device. The business's setting stays as it is, and so do prints. */
function DateSwitch() {
  const settings = useSettings()
  if (!settings) return null
  return (
    <div
      role="radiogroup"
      aria-label="Dates"
      className="mx-1 mb-1 grid grid-cols-3 gap-1 rounded-xl border border-border bg-background p-1"
    >
      {DATES.map(({ value, label, title }) => (
        <button
          key={value}
          type="button"
          role="radio"
          aria-checked={settings.date_display === value}
          aria-label={title}
          title={title}
          tabIndex={-1}
          onClick={() => setDateChoice(value)}
          className={cx(
            'rounded-lg py-1.5 text-xs font-medium transition-colors focus-visible:outline-2 focus-visible:outline-primary',
            settings.date_display === value
              ? 'bg-surface text-ink shadow-card'
              : 'text-muted hover:text-ink',
          )}
        >
          {label}
        </button>
      ))}
    </div>
  )
}

const MOTIONS: { value: MotionChoice; label: string; title: string }[] = [
  { value: 'full', label: 'Full', title: 'Numbers, charts and pages move when they change' },
  { value: 'reduced', label: 'Reduced', title: 'Nothing moves; changes show at once' },
]

/** Motion on this device. The phone's own "reduce motion" setting always wins. */
function MotionSwitch() {
  const [motion, setMotion] = useMotion()
  return (
    <div
      role="radiogroup"
      aria-label="Motion"
      className="mx-1 mb-1 grid grid-cols-2 gap-1 rounded-xl border border-border bg-background p-1"
    >
      {MOTIONS.map(({ value, label, title }) => (
        <button
          key={value}
          type="button"
          role="radio"
          aria-checked={motion === value}
          title={title}
          tabIndex={-1}
          onClick={() => setMotion(value)}
          className={cx(
            'rounded-lg py-1.5 text-xs font-medium transition-colors focus-visible:outline-2 focus-visible:outline-primary',
            motion === value ? 'bg-surface text-ink shadow-card' : 'text-muted hover:text-ink',
          )}
        >
          {label}
        </button>
      ))}
    </div>
  )
}

function HelpDialog({ open, onClose }: { open: boolean; onClose: () => void }) {
  return (
    <Modal open={open} onClose={onClose} title="Help and support" size="sm">
      <p className="mb-4 text-sm text-muted">
        Questions, a problem, or a plan to activate: we answer by phone and email.
      </p>
      <div className="space-y-2 text-sm">
        <a
          href={supportTel}
          className="flex items-center gap-2.5 rounded-xl border border-border p-3 hover:bg-soft"
        >
          <Phone className="size-4 text-muted" /> {SUPPORT_PHONE_LABEL}
        </a>
        <a
          href={`mailto:${SUPPORT_EMAIL}`}
          className="flex items-center gap-2.5 rounded-xl border border-border p-3 hover:bg-soft"
        >
          <Mail className="size-4 text-muted" /> {SUPPORT_EMAIL}
        </a>
      </div>
    </Modal>
  )
}

export function AccountMenu({ onShortcuts }: { onShortcuts: () => void }) {
  const user = useUser()
  const { current } = useWorkspace()
  const navigate = useNavigate()
  const signOut = useSignOut()
  const [help, setHelp] = useState(false)
  const email = user.email ?? ''
  const name = accountName(user)

  return (
    <>
      <Menu
        label="Account menu"
        title={name ?? email}
        width={272}
        trigger={initialsOf(user)}
        triggerClassName="ml-1 flex size-9 items-center justify-center rounded-full bg-primary-light text-sm font-semibold text-primary transition-shadow hover:ring-2 hover:ring-primary/30 aria-expanded:ring-2 aria-expanded:ring-primary"
      >
        <div className="flex items-center gap-2.5 px-2.5 pt-2.5 pb-2">
          <span
            aria-hidden
            className="flex size-10 shrink-0 items-center justify-center rounded-full bg-primary-light text-sm font-bold text-primary"
          >
            {initialsOf(user)}
          </span>
          <div className="min-w-0" translate="no">
            <div className="truncate text-sm font-semibold">{name ?? email}</div>
            {name && <div className="truncate text-xs text-muted">{email}</div>}
          </div>
        </div>
        {current && (
          <div className="px-2.5 pb-2">
            <span className="inline-block rounded-full bg-primary-light px-2 py-0.5 text-xs font-medium capitalize text-primary">
              {current.role}
            </span>
          </div>
        )}
        <MenuItem icon={<UserRound className="size-4" />} onSelect={() => navigate('/account')}>
          Your account
        </MenuItem>
        <MenuSeparator />
        <div className="px-2.5 pt-1 pb-1 text-section text-subtle">Theme</div>
        <ThemeSwitch />
        <div className="px-2.5 pt-1 pb-1 text-section text-subtle">Dates</div>
        <DateSwitch />
        <div className="px-2.5 pt-1 pb-1 text-section text-subtle">Motion</div>
        <MotionSwitch />
        <MenuSeparator />
        <MenuItem icon={<Settings className="size-4" />} onSelect={() => navigate('/settings')}>
          Settings
        </MenuItem>
        <MenuItem
          icon={<Smartphone className="size-4" />}
          onSelect={() => navigate('/settings?tab=device')}
        >
          This device and sync
        </MenuItem>
        <div className="hidden lg:block">
          <MenuItem icon={<Keyboard className="size-4" />} hint="?" onSelect={onShortcuts}>
            Keyboard shortcuts
          </MenuItem>
        </div>
        <MenuItem icon={<LifeBuoy className="size-4" />} onSelect={() => setHelp(true)}>
          Help and support
        </MenuItem>
        <MenuSeparator />
        <MenuItem danger icon={<LogOut className="size-4" />} onSelect={() => void signOut.start()}>
          Sign out
        </MenuItem>
      </Menu>
      <HelpDialog open={help} onClose={() => setHelp(false)} />
      {signOut.dialog}
    </>
  )
}
